import React, { useState } from 'react';
import { Button, Table, Modal, InputNumber, Input } from 'antd';
import { PlusOutlined, DeleteOutlined, UnorderedListOutlined } from '@ant-design/icons';
import type { FP631, FP632 } from '../../../services/pwp253Api';

interface Props {
  details: Partial<FP631>[];
  totalItems: number;
  isEditing: boolean;
  onAddDetail: () => void;
  onDeleteDetail: (index: number) => void;
  onUpdateDetail: (index: number, field: keyof FP631, value: any) => void;
  onAddSubDetail: (detailIdx: number) => void;
  onDeleteSubDetail: (detailIdx: number, subIdx: number) => void;
  onUpdateSubDetail: (detailIdx: number, subIdx: number, field: keyof FP632, value: any) => void;
}

const Tab2ColorDetails: React.FC<Props> = React.memo(({
  details, totalItems, isEditing,
  onAddDetail, onDeleteDetail, onUpdateDetail,
  onAddSubDetail, onDeleteSubDetail, onUpdateSubDetail,
}) => {
  const [subDetailModal, setSubDetailModal] = useState<{ visible: boolean; detailIdx: number }>({
    visible: false, detailIdx: -1,
  });

  const columns = [
    {
      title: '項數', dataIndex: 'fp631_02', width: 60,
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    {
      title: 'COLOR *', dataIndex: 'fp631_11', width: 100,
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val || ''}
          onChange={e => onUpdateDetail(idx, 'fp631_11', e.target.value)}
          disabled={!isEditing}
          size="small"
        />
      ),
    },
    {
      title: '缸號 *', dataIndex: 'fp631_12', width: 100,
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val || ''}
          onChange={e => onUpdateDetail(idx, 'fp631_12', e.target.value)}
          disabled={!isEditing}
          size="small"
        />
      ),
    },
    {
      title: '總疋數', dataIndex: 'fp631_14', width: 80,
      render: (val: number) => val || 0,
    },
    {
      title: '總碼數', dataIndex: 'fp631_15', width: 80,
      render: (val: number) => val || 0,
    },
    {
      title: '總重量', dataIndex: 'fp631_16', width: 80,
      render: (val: number) => val || 0,
    },
    {
      title: '備註', dataIndex: 'fp631_18', width: 120,
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val || ''}
          onChange={e => onUpdateDetail(idx, 'fp631_18', e.target.value)}
          disabled={!isEditing}
          size="small"
        />
      ),
    },
    {
      title: '顏色明細',
      width: 80,
      render: (_: any, __: any, idx: number) => (
        <Button
          size="small"
          icon={<UnorderedListOutlined />}
          onClick={() => setSubDetailModal({ visible: true, detailIdx: idx })}
          disabled={details.length === 0}
        >
          {(details[idx]?.sub_details?.length ?? details[idx]?.fp631_17) || 0}
        </Button>
      ),
    },
    ...(isEditing ? [{
      title: '',
      width: 50,
      render: (_: any, __: any, idx: number) => (
        <Button
          size="small"
          icon={<DeleteOutlined />}
          danger
          onClick={() => onDeleteDetail(idx)}
        />
      ),
    }] : []),
  ];

  const selectedDetail = subDetailModal.detailIdx >= 0 ? details[subDetailModal.detailIdx] : null;
  const subDetails = selectedDetail?.sub_details || [];

  const subColumns = [
    { title: '序號', width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
    {
      title: '碼數', dataIndex: 'fp632_12', width: 80,
      render: (val: number, _: any, idx: number) => (
        <InputNumber
          value={val ?? 0}
          onChange={v => onUpdateSubDetail(subDetailModal.detailIdx, idx, 'fp632_12', v ?? 0)}
          disabled={!isEditing}
          size="small"
          min={0}
        />
      ),
    },
    {
      title: '重量', dataIndex: 'fp632_13', width: 80,
      render: (val: number, _: any, idx: number) => (
        <InputNumber
          value={val ?? 0}
          onChange={v => onUpdateSubDetail(subDetailModal.detailIdx, idx, 'fp632_13', v ?? 0)}
          disabled={!isEditing}
          size="small"
          min={0}
        />
      ),
    },
    {
      title: '備註', dataIndex: 'fp632_14', width: 120,
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val || ''}
          onChange={e => onUpdateSubDetail(subDetailModal.detailIdx, idx, 'fp632_14', e.target.value)}
          disabled={!isEditing}
          size="small"
        />
      ),
    },
    ...(isEditing ? [{
      title: '',
      width: 50,
      render: (_: any, __: any, idx: number) => (
        <Button
          size="small"
          icon={<DeleteOutlined />}
          danger
          onClick={() => onDeleteSubDetail(subDetailModal.detailIdx, idx)}
        />
      ),
    }] : []),
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>總項數：{totalItems}</span>
        {isEditing && (
          <Button icon={<PlusOutlined />} size="small" onClick={onAddDetail}>
            明細新增
          </Button>
        )}
      </div>

      <Table
        dataSource={details.map((d, i) => ({ ...d, key: i }))}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 700 }}
      />

      <Modal
        title={`顏色子明細 — COLOR: ${selectedDetail?.fp631_11 || ''} 缸號: ${selectedDetail?.fp631_12 || ''}`}
        open={subDetailModal.visible}
        onCancel={() => setSubDetailModal({ visible: false, detailIdx: -1 })}
        footer={
          isEditing ? (
            <Button
              icon={<PlusOutlined />}
              onClick={() => onAddSubDetail(subDetailModal.detailIdx)}
            >
              新增子明細
            </Button>
          ) : null
        }
        width={600}
      >
        <Table
          dataSource={subDetails.map((s, i) => ({ ...s, key: i }))}
          columns={subColumns}
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  );
});

export default Tab2ColorDetails;
